/**
 * LeadsLenses — the view-switcher SHELL for the Leads "Lenses" pilot
 * (AD-046). Renders BESIDE the live screen, never replacing it: the
 * "Existing" tab IS the unmodified <Leads/> (its own subscription, byte-for-
 * byte). The four NEW views (List/Board/Calendar/Map) share ONE lifted
 * `listenLeads` stream + ONE shared <LeadDetailDrawer> (which mounts the
 * unmodified <LeadDetail> + ChatSurface). Every view is wrapped in
 * <LensErrorBoundary> so one crash can never white-screen the route — the
 * Existing tab always stays reachable.
 *
 * Mounted by App.tsx ONLY when `leadsLensesEnabled(role)`; flag-off users
 * render today's <Leads/> directly (so the live screen is untouched).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { visibilityFor } from '../lib/roles';
import { canManageTrash } from '../lib/trash';
import { useIsMobile } from '../lib/useIsMobile';
import Leads from './Leads';
import ViewSwitcher, { type LensView } from '../components/leadviews/ViewSwitcher';
import LensErrorBoundary from '../components/leadviews/LensErrorBoundary';
import LeadListView from '../components/leadviews/LeadListView';
import NeedsAttentionStrip from '../components/leadviews/NeedsAttentionStrip';
import LeadBoardView from '../components/leadviews/LeadBoardView';
import LeadCalendarView from '../components/leadviews/LeadCalendarView';
import LeadMapView from '../components/leadviews/LeadMapView';
import LeadDetailDrawer from '../components/LeadDetailDrawer';
import LeadRecordPage from '../components/leadviews/LeadRecordPage';
import { setLensesOptIn } from '../lib/featureFlags';
import {
  listenLeads,
  trashLeadDoc,
  updateLead,
} from '../lib/leadStore';
import { BLANK_MANUAL, createInboundLead } from '../lib/leadCreate';
import type { ContactLite } from '../lib/contactMatch';
import {
  clientRowsFromProjects,
  listenClients,
  listenPartners,
  listenProjects,
  matchLeadToClient,
  mergeClientRows,
  type ClientRow,
  type LiveClient,
  type LivePartner,
  type LiveProject,
} from '../lib/projectStore';
import {
  canTransition,
  correctLeadState,
  resolveDetail,
  transitionLead,
  type Lead,
  type LeadState,
} from '../lib/leads';
import type { FStage } from '../lib/leadPipeline';

export default function LeadsLenses() {
  const { role, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  // Phase 3b — `/leads/:id` deep-link. Same LeadsGate element as `/leads`, so
  // this component stays MOUNTED across the two paths; `routeId` simply
  // switches the render SHAPE (board/peek ↔ full-page record).
  const { id: routeId } = useParams();
  const vis = visibilityFor(role);
  const isMobile = useIsMobile();

  const [activeView, setActiveView] = useState<LensView>('existing');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [clientProjectsRaw, setClientProjectsRaw] = useState<LiveProject[]>([]);
  const [clientMasters, setClientMasters] = useState<LiveClient[]>([]);
  const [knownPartners, setKnownPartners] = useState<LivePartner[]>([]);

  const actorName = user?.displayName || user?.email?.split('@')[0] || 'You';
  const isAdmin = role === 'admin';
  const isTeamView = role === 'admin' || role === 'staff';
  const mayTrash = canManageTrash(role);
  const nowISO = useMemo(() => new Date().toISOString(), [leads.length]);

  // The ONE lifted stream the four new views share (only when a new view is
  // active — Existing has its own subscription so we don't double-read when
  // sitting on Existing). Newest first.
  useEffect(() => {
    // Subscribe for the new views AND whenever a `/leads/:id` deep-link is
    // open (the full-page record needs the stream even if the user landed on
    // the Existing tab). The no-param Existing case is unchanged.
    if (activeView === 'existing' && !routeId) return;
    const unsub = listenLeads(
      (live) => setLeads(live),
      () => toast('Could not load leads (check connection)'),
    );
    return unsub;
  }, [activeView, routeId, toast]);

  // Directories for the shared drawer's <LeadDetail> (convert picker etc.).
  useEffect(() => {
    if (activeView === 'existing' && !routeId) return;
    const u1 = listenProjects((p) => setClientProjectsRaw(p), () => {});
    const u2 = listenClients((m) => setClientMasters(m), () => {});
    const u3 = listenPartners((rows) => setKnownPartners(rows.filter((p) => !p.archived)), () => {});
    return () => {
      u1();
      u2();
      u3();
    };
  }, [activeView, routeId]);

  // Keep selection in step with the route so peek ↔ full-page stay aligned:
  // opening `/leads/:id` selects that lead; returning to `/leads` preserves it
  // (selectedId is untouched here), so the peek reopens for the same lead.
  useEffect(() => {
    if (routeId) setSelectedId(routeId);
  }, [routeId]);

  const knownClients: ClientRow[] = useMemo(
    () => mergeClientRows(clientRowsFromProjects(clientProjectsRaw, true), clientMasters, true),
    [clientProjectsRaw, clientMasters],
  );

  const selected = resolveDetail(leads, selectedId);

  /* persist a pure-mutated lead through the SAME rules-whitelisted path. */
  async function persist(next: Lead, what: string) {
    // optimistic local echo so the drawer/board update instantly; the stream
    // re-confirms shortly after.
    setLeads((prev) => prev.map((l) => (l.id === next.id ? next : l)));
    try {
      await updateLead(next, actorName, what);
    } catch (e) {
      toast(`Save failed: ${(e as Error).message}`);
    }
  }

  function onChange(next: Lead) {
    void persist(next, 'Lead updated (lens)');
  }
  function onBoardState(lead: Lead, to: LeadState) {
    try {
      void persist(transitionLead(lead, to, new Date().toISOString()), `Lead → ${to}`);
    } catch {
      /* illegal — guarded upstream, ignored */
    }
  }
  function onBoardFstage(lead: Lead, to: FStage) {
    void persist({ ...lead, fstage: to, updatedAt: new Date().toISOString() }, `Fiverr → ${to}`);
  }
  // CONFIRMED out-of-flow Standard board move (user accepted the regression in
  // the board's confirm dialog). A legal hop goes through `transitionLead`; an
  // illegal/regression hop goes through `correctLeadState` — the ONLY other
  // writer of `state` (streamline Phase 1b: no direct state writes anywhere).
  // It validates the target (refusing `converted` — conversion must mint the
  // client + project via the convert bridge, never a drag), stamps the
  // `lastStateCorrection` audit marker, and preserves every other
  // timestamp/field (no clearing).
  function onBoardMove(lead: Lead, to: LeadState) {
    const now = new Date().toISOString();
    if (canTransition(lead.state, to)) {
      void persist(transitionLead(lead, to, now), `Lead → ${to}`);
    } else {
      try {
        void persist(
          correctLeadState(lead, to, now),
          `Lead moved → ${to} (correction)`,
        );
      } catch (e) {
        toast((e as Error).message);
      }
    }
  }
  // CONFIRMED out-of-flow Fiverr board move — direct fstage correction (the
  // Fiverr forward flow has no canTransition spine; a backward index move is a
  // straight snapshot-field write, same seam).
  function onBoardFstageMove(lead: Lead, to: FStage) {
    void persist(
      { ...lead, fstage: to, updatedAt: new Date().toISOString() },
      `Fiverr moved → ${to} (correction)`,
    );
  }
  async function onTrash(lead: Lead) {
    try {
      await trashLeadDoc(lead, actorName, new Date().toISOString());
      setSelectedId(null);
    } catch (e) {
      toast(`Trash failed: ${(e as Error).message}`);
    }
  }

  function openDrawerFor(id: string) {
    setSelectedId(id);
  }

  // "+ New Lead" on the List/Board lens toolbars (parity audit GAP 1,
  // 2026-07-03) — the SAME shared create path the Existing screen uses
  // (lib/leadCreate: AD-039 contact-spine match → makeLead → createLead →
  // reverse back-ref). On success the new lead's drawer opens — and since
  // the blank manual lead has no phone/email yet, the record shows the
  // identity capture FIRST (needsIdentityCapture).
  const [creating, setCreating] = useState(false);
  async function onNewLead() {
    if (creating) return;
    setCreating(true);
    try {
      const id = await createInboundLead(
        BLANK_MANUAL(),
        clientMasters as unknown as ContactLite[],
        leads.length + 1,
        actorName,
      );
      setSelectedId(id);
    } catch (e) {
      toast(`Could not create lead: ${(e as Error).message}`);
    } finally {
      setCreating(false);
    }
  }

  // ── Phase 3b: full-page record SHAPE (`/leads/:id`) ──────────────────────
  // When the route carries an id, render the 3-column full-page record INSTEAD
  // of the switcher/board/drawer. Kept inside a `lenses-root` wrapper so the
  // Phase 0/2 scoped CSS still applies and the AppShell layout stays consistent.
  if (routeId) {
    const fp = resolveDetail(leads, routeId);
    if (!fp) {
      return (
        <div
          className="lenses-root"
          style={{
            display: 'flex',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--ink-mute, #6B7280)',
            fontSize: 13,
          }}
        >
          Loading…
        </div>
      );
    }
    return (
      <div className="lenses-root" style={{ height: '100%' }}>
        <LensErrorBoundary label="lead record" onReset={() => navigate('/leads')}>
          <LeadRecordPage
            lead={fp}
            onChange={onChange}
            actorName={actorName}
            isTeamView={isTeamView}
            isAdmin={isAdmin}
            mayTrash={mayTrash}
            onTrash={onTrash}
            knownClients={knownClients}
            knownPartners={knownPartners}
            liveProjects={clientProjectsRaw}
            clientMatch={matchLeadToClient(fp, clientMasters)}
            contactPool={clientMasters}
            canCommit={vis.canCommit}
            seesMoney={vis.seesMoneyInternals}
            isMobile={isMobile}
            onState={onBoardState}
            onFstage={onBoardFstage}
            onBack={() => navigate('/leads')}
          />
        </LensErrorBoundary>
      </div>
    );
  }

  return (
    // ONE PAGE SCROLL (owner correction 2026-07-03 — "too shabby"): the shell
    // no longer fixes its height and scrolls an inner wrapper (a second
    // scroller nested inside the app's one `.work` scroller — hard-rule
    // violation, and the Existing screen inside it clipped its inbox to a
    // tiny box). Every lens now flows to natural height; `.work` owns the
    // ONE vertical scroll. (The `/leads/:id` full-page record branch above
    // is a separate viewport surface and is unchanged.)
    <div className="lenses-root" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Phase 3a — the OVERDUE next-action strip, on the SHARED shell so every
          lens (Existing included) sees it. Self-contained; renders nothing
          when nothing is overdue. */}
      <NeedsAttentionStrip />
      {/* switcher bar — wraps on narrow screens (Vera HIGH 2026-07-03: the
          "Lenses: on ✕" toggle overflowed a 390px viewport and gave the page
          a 54px sideways pan; wrapping keeps every control on-screen with
          zero horizontal overflow). */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '8px 12px',
          borderBottom: '1px solid var(--rule, #E5E7EB)',
          flex: '0 0 auto',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <ViewSwitcher active={activeView} onChange={setActiveView} />
          {/* + New Lead — List/Board parity (GAP 1). Same create path as the
              Existing screen; commit-gated like every other write here. */}
          {(activeView === 'list' || activeView === 'board') && vis.canCommit && (
            <button
              type="button"
              data-testid="lens-new-lead"
              onClick={() => void onNewLead()}
              disabled={creating}
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: '#fff',
                background: 'var(--accent, #008069)',
                border: '1px solid var(--accent, #008069)',
                borderRadius: 'var(--r-pill, 999px)',
                padding: '4px 14px',
                cursor: creating ? 'wait' : 'pointer',
                opacity: creating ? 0.7 : 1,
              }}
            >
              {creating ? 'Creating…' : '+ New Lead'}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            setLensesOptIn(false);
            window.location.reload();
          }}
          title="Turn the Lenses pilot off for your session"
          style={{
            fontSize: 10.5,
            color: 'var(--ink-mute, #6B7280)',
            background: 'transparent',
            border: '1px solid var(--rule, #E5E7EB)',
            borderRadius: 'var(--r-pill, 999px)',
            padding: '3px 10px',
            cursor: 'pointer',
          }}
        >
          Lenses: on ✕
        </button>
      </div>

      {/* active view — natural height, no inner scroller (one page scroll) */}
      <div style={{ flex: '1 1 auto', minHeight: 0 }}>
        {activeView === 'existing' ? (
          // The unmodified live screen — its OWN subscription, byte-for-byte.
          <Leads />
        ) : (
          <LensErrorBoundary label={activeView} onReset={() => setActiveView('existing')}>
            {activeView === 'list' && (
              <LeadListView
                leads={leads}
                onSelect={openDrawerFor}
                selectedId={selectedId}
                seesMoney={vis.seesMoneyInternals}
                nowISO={nowISO}
              />
            )}
            {activeView === 'board' && (
              <LeadBoardView
                leads={leads}
                onSelect={openDrawerFor}
                selectedId={selectedId}
                onState={onBoardState}
                onFstage={onBoardFstage}
                onMoveBack={onBoardMove}
                onFstageMoveBack={onBoardFstageMove}
                seesMoney={vis.seesMoneyInternals}
                canCommit={vis.canCommit}
                isMobile={isMobile}
              />
            )}
            {activeView === 'calendar' && (
              <LeadCalendarView leads={leads} onSelect={openDrawerFor} selectedId={selectedId} isMobile={isMobile} nowISO={nowISO} />
            )}
            {activeView === 'map' && (
              <LeadMapView leads={leads} onSelect={openDrawerFor} selectedId={selectedId} isMobile={isMobile} />
            )}
          </LensErrorBoundary>
        )}
      </div>

      {/* shared detail drawer (only for the new views; Existing has its own) */}
      {activeView !== 'existing' && selected && (
        <LensErrorBoundary label="lead detail" onReset={() => setSelectedId(null)}>
          <LeadDetailDrawer
            lead={selected}
            onClose={() => setSelectedId(null)}
            onChange={onChange}
            authorName={actorName}
            isTeamView={isTeamView}
            isAdmin={isAdmin}
            mayTrash={mayTrash}
            onTrash={onTrash}
            knownClients={knownClients}
            knownPartners={knownPartners}
            liveProjects={clientProjectsRaw}
            clientMatch={selected ? matchLeadToClient(selected, clientMasters) : null}
            contactPool={clientMasters}
            canCommit={vis.canCommit}
            isMobile={isMobile}
            onOpenFull={() => navigate(`/leads/${selected.id}`)}
          />
        </LensErrorBoundary>
      )}
    </div>
  );
}
